import { Maybe } from 'types/graphql-apollo-v2';
import dayjs from 'dayjs';
import calendar from 'dayjs/plugin/calendar';
import { PAGINATION_PAGE_SIZE } from './config';

dayjs.extend(calendar);

export const isString = (value: any): value is string =>
  Object.prototype.toString.call(value) === '[object String]';

export const isObject = <T = object>(value: any): value is T => {
  const type = typeof value;
  return (
    value != null &&
    (type === 'object' || type === 'function') &&
    !Array.isArray(value)
  );
};

export const convertISODateToJSDate = (dateISO?: Maybe<string> | null) => {
  if (!dateISO) return null;
  return new Date(dateISO);
};

export const formatDateRecentTime = (dateISO: string | number | null) => {
  if (!dateISO) return '-';
  const date = new Date(dateISO);
  if (Number.isNaN(date.getTime())) return '-';

  return dayjs(date).calendar(undefined, {
    sameDay: '[Today,] HH:mm',
    lastDay: '[Yesterday,] HH:mm',
    lastWeek: '[Last] dddd[,] HH:mm',
    sameElse: 'DD/MM/YYYY[,] HH:mm',
  });
};

export const compareISODates = (
  dateISO1: Maybe<string>,
  dateISO2: Maybe<string>
) => {
  if (!dateISO1) return true;
  if (!dateISO2) return false;
  return dateISO1 < dateISO2;
};

export const addSpaceBeforeCapitalLetters = (str: string) =>
  str.replace(/([A-Z])/g, ' $1').trim();

export const sortStringOrUndefined = (
  a: string | undefined | null,
  b: string | undefined | null
) => {
  if (!b && !a) return 0;
  if (!b) return -1;
  if (!a) return 1;
  return a.localeCompare(b);
};

/** Used in a filter function to get only the unique values */
export const onlyUnique = (value: any, index: number, self: any) =>
  self.indexOf(value) === index;

/** Validates an email using Regex. */
export const validateEmail = (email: string) => {
  const regEmail =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
  return regEmail.test(email);
};

/** Removes any white spaces after a comma character (,). */
export const removeTrailingSpaceAfterComma = (str: string) =>
  str.replace(/\s*,\s*/g, ',');

export const getFirstLetterFromEmail = (email: string) => {
  const firstLetter =
    email?.split('@')[0].split('.')[0]?.charAt(0) ||
    email?.split('@')[0].charAt(0);
  return firstLetter;
};

export const getSecondLetterFromEmail = (email: string) => {
  const secondLetter =
    email?.split('@')[0].split('.')[1]?.charAt(0) ||
    email?.split('@')[0].charAt(0);
  return secondLetter;
};

export const wordFromUpperCaseToCapitilized = (word: string) =>
  word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();

/** Creates a refId for a SchemaCollection in the right format. */
export function createRefId(
  space: string,
  dataModel: string,
  dataModelVersion = '1'
) {
  return `${space}:${dataModel}:${dataModelVersion}`;
}

/**
 * Extracts FDM data model details from SchemaCollection's refId.
 * Format should be {space}:{dataModel}:{dataModelVersion}, like 'star-wars:star_wars:1'.
 */
export function parseRefId(refId?: string | null) {
  const [space, dataModel, dataModelVersion = '1'] = (refId || '').split(':');

  return {
    dataModel: dataModel ?? space.replaceAll('-', '_'),
    dataModelVersion,
    space,
  };
}

/** Returns a subset of items for a given page. */
export function getPageItems<T>(
  items: T[],
  page: number,
  pageSize = PAGINATION_PAGE_SIZE
) {
  const start = (page - 1) * pageSize;
  const end = page * pageSize;

  return items.slice(start, end);
}
